import React from 'react';
// import propTypes from 'prop-types';
import { clamp } from 'lodash';
import classnames from 'classnames';

import Attendance from '@profile_common/images/svg/Attendance.svg';
import BadgedPerformance from '@profile_common/ui/BadgedPerformance';
import BestRank from '@profile/images/svg/BestRank.svg';
import RecentContestRank from '@profile/images/svg/RecentContestRank.svg';
import Problems from '@profile_common/images/svg/Problem.svg';
import { Progressbar } from '@common/ui/general';
import ProgressedPerformance from '@profile_common/ui/ProgressedPerformance';
import ReputationPoints from '@profile_common/images/svg/ReputationPoint.svg';
import Streak from '@profile_common/images/svg/Streak.svg';

function injectValues(performance, elem) {
  const contestDetails = performance[elem.key];
  const {
    rank, totalUsers, startTime,
  } = contestDetails;

  return {
    ...elem,
    stat: `${rank || '-'}/${totalUsers || '-'}`,
    suffix: `Held on ${startTime || '-'}`,
  };
}

const data = [
  {
    title: 'Reputation Points',
    icon: ReputationPoints,
    stat: '342 Bits',
    suffix: null,
    border: true,
    injectValues: false,
    key: 'reputation_points',
  }, {
    title: 'Current Streak',
    icon: Streak,
    stat: '2',
    suffix: null,
    border: false,
    injectValues: false,
    key: 'current_streak',
  }, {
    title: 'Best Streak',
    icon: Streak,
    stat: '14',
    suffix: null,
    border: true,
    injectValues: false,
    key: 'best_streak',
  }, {
    title: 'Problems Solved',
    icon: Problems,
    stat: '42',
    suffix: null,
    border: false,
    injectValues: false,
    key: 'problems_solved',
  }, {
    title: 'Recent Contest Rank',
    icon: RecentContestRank,
    stat: '3/186',
    suffix: 'Held on 10 May 2019',
    border: true,
    shouldRenderContestRank: true,
    injectValues: true,
    key: 'recent_contest_rank',
  }, {
    title: 'Best Contest Rank',
    icon: BestRank,
    stat: '1/186',
    suffix: 'Held on 5 May 2019',
    border: false,
    shouldRenderContestRank: true,
    injectValues: true,
    key: 'best_contest_rank',
  },
];

function ProfilePerformance({
  performance, profileACL,
}) {
  function transformPerformance(elem) {
    if (!Object.prototype.hasOwnProperty.call(performance, elem.key)) {
      return null;
    }

    if (elem.injectValues) {
      return injectValues(performance, elem);
    }

    return {
      ...elem,
      stat: performance[elem.key] || '-',
    };
  }

  const transformedPerformance = data.map(transformPerformance);
  const {
    attendance,
    problem_solved_percentage: solvedPercentage,
    // previous_contest_details: previousContestData,
  } = performance;
  const previousContestData = [];

  function badgeCellUI(elemData, key) {
    const {
      title, icon, stat, suffix, border,
    } = elemData;

    return (
      <div className="profile-performance__cell" key={key}>
        <BadgedPerformance
          title={title}
          icon={icon}
          stat={stat}
          suffix={suffix}
          border={border}
          className=""
        />
      </div>
    );
  }

  function progressedPerformanceUI() {
    if (profileACL.isPublic()) {
      return null;
    }

    return (
      <div className="profile-performance__wrapped-row">
        <ProgressedPerformance
          title="Problem Solved %"
          subtitle="(Try to keep this above 70%)."
          icon={Problems}
          progress={clamp(solvedPercentage, 0, 100)}
          trend="up"
        />
        <ProgressedPerformance
          title="Attendance %"
          subtitle="(Try to keep this above 70%)."
          icon={Attendance}
          progress={attendance}
          trend="down"
        />
      </div>
    );
  }

  function progressBarUI({
    rank, totalUser,
  }, key) {
    const coverage = 100 - Math.min(100, ((rank - 1) / totalUser) * 100);

    return (
      <td className="profile-contest-stat__row-cell" key={key}>
        <div className="profile-contest-stat__cell-row">
          <Progressbar
            limit={100}
            value={coverage}
            className="profile-contest-stat__progress-bar"
            renderValue={false}
            progressbarClassName="progressbar__color--blue"
            orientation="vertical"
          />
        </div>
      </td>
    );
  }

  function previousContestUI() {
    if (previousContestData.length === 0) {
      return null;
    }

    return (
      <div className="profile-contest-stat__container">
        <table
          className="profile-contest-stat__table"
          cellSpacing={0}
          cellPadding={0}
        >
          <tbody>
            <tr className="profile-contest-stat__row">
              {previousContestData.map(progressBarUI)}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  function contestRankCellUI() {
    if (profileACL.isPublic()) {
      return null;
    }

    return (
      <div className="profile-performance">
        <h3>Contest Rank</h3>
        <div
          className={classnames(
            'profile-performance__container',
            'profile-performance__container--rank',
          )}
        >
          <div
            className={classnames(
              'profile-performance__row',
              'profile-performance__row--no-border',
            )}
          >
            {
              transformedPerformance.map((elemData, key) => {
                if (!elemData
                    || !Object.prototype.hasOwnProperty.call(
                      elemData, 'shouldRenderContestRank',
                    )
                ) {
                  return null;
                }
                return badgeCellUI(elemData, key);
              })
            }
          </div>
        </div>
        {previousContestUI()}
      </div>
    );
  }

  return (
    <>
      {contestRankCellUI()}
      <div className="profile-performance">
        <h3>Overall Performance</h3>
        <div className="profile-performance__container">
          <div
            className={classnames(
              'profile-performance__row',
              {
                'profile-performance__row--no-border':
                  (profileACL.isPublic()),
              },
            )}
          >
            {
              transformedPerformance.map((elemData, key) => {
                if (!elemData) {
                  return null;
                }
                if ((profileACL.isMentor()
                    || profileACL.isPrivate())
                    && Object.prototype.hasOwnProperty.call(
                      elemData, 'shouldRenderContestRank',
                    )
                ) {
                  return null;
                }
                return badgeCellUI(elemData, key);
              })
            }
          </div>
          {progressedPerformanceUI()}
        </div>
      </div>
    </>
  );
}

export default ProfilePerformance;
